import { apiClient } from './api'

export type Stage = 'idea' | 'discovery' | 'development' | 'launch' | 'growth' | 'mature' | 'sunset'
export type IdeaStatus = 'new' | 'exploring' | 'planned' | 'shipped' | 'rejected'
export type IdeaSource = 'customer' | 'sales' | 'support' | 'internal' | 'data'
export type Health = 'at_risk' | 'watch' | 'on_track' | null

export interface ProductProject {
  id: number; name: string; status: string; progress: number
  start_date: string | null; end_date: string | null; health: Health; health_reason: string | null
}
export interface ProductMetric {
  id: number; name: string; unit: string | null; target: number | null; direction: 'up' | 'down'
  latest: number | null; previous: number | null; latest_on: string | null; on_target: boolean | null
  history?: Array<{ id: number; value: number; recorded_on: string; note: string | null }>
}
export interface ProductSummary {
  id: number; name: string; description: string | null; vision: string | null; stage: Stage
  owner_id: number | null; owner_name: string | null; portfolio_id: number | null
  projects: ProductProject[]; open_projects: number; at_risk: number; watch: number
  next_delivery: string | null; ideas_open: number; metrics: ProductMetric[]
}
export interface ProductIdea {
  id: number; title: string; description: string | null; source: IdeaSource | null; status: IdeaStatus
  product_id: number | null; product_name: string | null
  reach: number | null; impact: number | null; confidence: number | null; effort: number | null; rice: number | null
  submitted_by: string | null; request_id: number | null; created_at: string | null
}
export interface ProductDetail extends ProductSummary {
  links: Array<{ type: 'project' | 'program'; id: number; name: string }>
  milestones: Array<{ id: number; name: string; project_id: number; project_name: string; due_date: string; done: boolean }>
  ideas: ProductIdea[]
  portfolio_name: string | null
}
export interface ProductsOverview {
  products: ProductSummary[]
  stages: Stage[]
  summary: { products: number; by_stage: Record<Stage, number>; at_risk: number; ideas_to_triage: number }
}
export interface Roadmap {
  quarters: Array<{ key: string; label: string; start: string }>
  lanes: Array<{ product_id: number; name: string; stage: Stage; items: Array<ProductProject & { from_col: number; to_col: number }> }>
}
export interface ProductInput {
  name?: string; description?: string | null; vision?: string | null; stage?: Stage
  owner_id?: number | null; portfolio_id?: number | null
}
export interface IdeaInput {
  title?: string; description?: string | null; product_id?: number | null; source?: IdeaSource | null
  reach?: number | null; impact?: number | null; confidence?: number | null; effort?: number | null; status?: IdeaStatus
}

const B = '/api/v1/products'

export const productsService = {
  overview: () => apiClient.get<ProductsOverview>(B).then((r) => r.data),
  get: (id: number) => apiClient.get<ProductDetail>(`${B}/${id}`).then((r) => r.data),
  create: (body: ProductInput) => apiClient.post<ProductDetail>(B, body).then((r) => r.data),
  update: (id: number, body: ProductInput) => apiClient.put<ProductDetail>(`${B}/${id}`, body).then((r) => r.data),
  remove: (id: number) => apiClient.delete(`${B}/${id}`).then(() => undefined),
  link: (id: number, type: 'project' | 'program', entityId: number) =>
    apiClient.post<ProductDetail>(`${B}/${id}/links`, { type, id: entityId }).then((r) => r.data),
  unlink: (id: number, type: 'project' | 'program', entityId: number) =>
    apiClient.delete<ProductDetail>(`${B}/${id}/links/${type}/${entityId}`).then((r) => r.data),
  roadmap: (quarters = 4) => apiClient.get<Roadmap>(`${B}/roadmap`, { params: { quarters } }).then((r) => r.data),
  ideas: (productId?: number) =>
    apiClient.get<ProductIdea[]>(`${B}/ideas`, { params: { product_id: productId } }).then((r) => r.data),
  createIdea: (body: IdeaInput) => apiClient.post<ProductIdea>(`${B}/ideas`, body).then((r) => r.data),
  updateIdea: (id: number, body: IdeaInput) => apiClient.put<ProductIdea>(`${B}/ideas/${id}`, body).then((r) => r.data),
  deleteIdea: (id: number) => apiClient.delete(`${B}/ideas/${id}`).then(() => undefined),
  ideaToIntake: (id: number) => apiClient.post<{ request_id: number }>(`${B}/ideas/${id}/to-intake`).then((r) => r.data),
  addMetric: (id: number, body: { name: string; unit?: string | null; target?: number | null; direction?: 'up' | 'down' }) =>
    apiClient.post<ProductDetail>(`${B}/${id}/metrics`, body).then((r) => r.data),
  deleteMetric: (metricId: number) => apiClient.delete(`${B}/metrics/${metricId}`).then(() => undefined),
  recordValue: (metricId: number, body: { value: number; recorded_on?: string; note?: string | null }) =>
    apiClient.post<ProductDetail>(`${B}/metrics/${metricId}/values`, body).then((r) => r.data),
}

export const STAGE_LABEL: Record<Stage, string> = {
  idea: 'Idea', discovery: 'Discovery', development: 'In development', launch: 'Launching',
  growth: 'Growth', mature: 'Mature', sunset: 'Sunset',
}
export const IDEA_STATUS_LABEL: Record<IdeaStatus, string> = {
  new: 'New', exploring: 'Exploring', planned: 'Planned', shipped: 'Shipped', rejected: 'Rejected',
}
